import type { Messages } from '../catalogue.en'

/**
 * Clinical: the encounter shell, schema-driven protocol authoring and
 * referrals.
 *
 * The largest surface because it carries the most refusals, and each refusal
 * keeps its own sentence here for the same reason it has one in English: a
 * clinician who reads one generic failure reloads instead of acting.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const clinical: Partial<Messages> = {
  'clinical.parameters.title': "Параметры приёма",
  'clinical.parameters.unset': "Не выбрано",
  'clinical.parameters.ruPlace': "Место проведения",
  'clinical.parameters.ruForm': "Форма оказания помощи",
  'clinical.parameters.ruType': "Вид медицинской помощи",
  'clinical.parameters.ruVisitKind': "Первичность приёма",
  'clinical.parameters.ruCircumstances': "Обстоятельства обращения",
  'clinical.parameters.ilSetting': "Условия оказания помощи",
  'clinical.parameters.ilContact': "Тип контакта (необязательно)",
  'clinical.parameters.ilPriority': "Приоритет (необязательно)",
  'clinical.parameters.ilService': "Вид услуги (необязательно)",
  'clinical.parameters.ruHint': "Эти сведения относятся к приёму и общие для всех его протоколов. Перед завершением документации выберите все значения. Состояние пациента и исход заполняются в протоколе.",
  'clinical.parameters.ilHint': "Выберите условия оказания помощи. Тип контакта, приоритет и вид услуги необязательны. Параметры относятся к приёму и общие для всех его протоколов.",
  'clinical.parameters.suggested': "Вид помощи или услуги подставлен из вашего последнего подтверждённого выбора для этого протокола. Проверьте его перед сохранением.",
  'clinical.parameters.unbound': "Для поля не настроен подходящий справочник. Обратитесь к администратору.",
  'clinical.parameters.noProfile': "Параметры приёма недоступны: выберите для клиники поддерживаемый национальный профиль терминологии.",
  'clinical.parameters.loading': "Загрузка параметров приёма…",
  'clinical.parameters.loadFailed': "Не удалось загрузить параметры приёма.",
  'clinical.parameters.unsaved': "Несохранённые параметры приёма",
  'clinical.parameters.save': "Сохранить параметры",
  'clinical.parameters.saving': "Сохранение параметров…",
  'clinical.parameters.saved': "Параметры приёма сохранены.",
  'clinical.parameters.saveFailed': "Не удалось сохранить параметры. Ваши изменения сохранены в форме.",
  'clinical.parameters.invalid': "Проверьте выбранные значения параметров. Ваши изменения сохранены в форме.",
  'clinical.parameters.required': "Перед завершением документации заполните и сохраните параметры приёма.",
  'clinical.parameters.changed': "Параметры приёма изменены в другом окне. Перед сохранением загрузите сохранённую версию.",
  'clinical.parameters.profileChanged': "Национальный профиль терминологии изменён. Загрузите сохранённые параметры и проверьте выбор.",
  'clinical.parameters.readOnly': "Параметры приёма доступны только для чтения.",
  'clinical.parameters.resetPreference': "Забыть запомненный вид помощи или услуги",
  'clinical.parameters.resetFailed': "Не удалось удалить запомненный выбор.",
  'clinical.parameters.preferenceReset': "Запомненный выбор удалён. Параметры текущего приёма сохранены.",
  'clinical.parameters.reload': "Загрузить сохранённые параметры",
  'clinical.parameters.reloadFailed': "Не удалось загрузить сохранённые параметры. Ваши изменения сохранены в форме.",
  'clinical.parameters.reloaded': "В форме показаны сохранённые параметры.",
  'clinical.startVisit': 'Начать приём',
  'clinical.startingVisit': 'Запуск',
  'clinical.openVisit': 'Открыть приём',
  'clinical.openingVisit': 'Открытие',
  'clinical.notStartable': 'Нельзя начать',
  'clinical.startMissingCapability': 'Нет доступа к приёму',
  'clinical.startForbidden': 'У вас нет прав начать этот приём.',
  'clinical.startStale': 'Эта запись на приём больше недоступна.',
  'clinical.alreadyStartedMissingLink': 'Приём начат, но ссылка на него недоступна.',
  'clinical.startConflict': 'Эта запись на приём уже связана иначе. Обновите расписание.',
  'clinical.startInvalid': 'Не удалось начать приём из этой записи на приём.',
  'clinical.startMalformed': 'Ответ по приёму был неполным.',
  'clinical.startUnavailable': 'Сейчас не удалось открыть приём. Попробуйте снова.',
  'clinical.startFailed': 'Не удалось открыть приём.',
  'clinical.encounterTitle': 'Приём',
  'clinical.loading': 'Загрузка приёма',
  'clinical.practitionerRequired': 'Требуется учётная запись клинициста',
  'clinical.practitionerRequiredDetail': 'Этот приём может открыть только учётная запись, связанная с врачом.',
  'clinical.practitioner': 'Врач',
  'clinical.appointmentTime': 'Время приёма',
  'clinical.caseCode': 'Код случая',
  'start.title': 'Начать приём',
  'start.detail': 'Выберите, к какому случаю относится этот приём, или откройте новый. Каждый приём относится к случаю.',
  'start.promptLabel': 'Начать приём для {name}',
  'start.chooseCase': 'Случай для этого приёма',
  'start.caseNoneOpen': 'У этого пациента нет открытого случая, поэтому этот приём создаёт новый.',
  'start.caseChoose': 'Выберите случай, к которому относится этот приём.',
  'start.confirm': 'Начать',
  'start.starting': 'Запуск…',
  'start.cancel': 'Отмена',
  'caseChoice.new': 'Создать новый случай',
  'caseChoice.preselected': 'Выбран единственный открытый случай.',
  'caseChoice.titleLabel': 'Название случая',
  'caseChoice.commentLabel': 'Комментарий',
  'caseChoice.startLabel': 'Дата начала',
  'caseChoice.endLabel': 'Дата окончания',
  'caseChoice.titleRequired': 'Необходимо указать название случая.',
  'caseChoice.ineligibleHidden': 'Не показаны завершённые или отменённые случаи ({count}): закрытый случай не может принять новый приём.',
  'clinical.status.inProgress': 'В ходе выполнения',
  'clinical.status.finished': 'Завершён',
  'clinical.status.enteredInError': 'Внесён по ошибке',
  'clinical.invalidDeepLink': 'Ссылка на приём недействительна',
  'clinical.invalidDeepLinkDetail': 'Откройте приём из расписания снова.',
  'clinical.staleEvent': 'Ссылка на приём устарела',
  'clinical.staleEventDetail': 'Обновите расписание и откройте приём снова.',
  'clinical.encounterNotFound': 'Приём не найден',
  'clinical.encounterNotFoundDetail': 'Этот приём недоступен в вашей клинике.',
  'clinical.sessionExpiredDetail': 'Войдите снова, чтобы продолжить.',
  'clinical.unavailable': 'Не удалось загрузить страницу',
  'clinical.unavailableDetail': 'Проверьте соединение и попробуйте снова.',
  'clinical.invalidResponse': 'Ответ сервера был неполным',
  'clinical.invalidResponseDetail': 'Попробуйте снова или попросите администратора проверить Core.',

  'record.title': 'Клиническая запись',
  'record.detailTitle': 'Подробности клинической записи',
  'record.loading': 'Загрузка клинической записи…',
  'record.loadFailed': 'Не удалось загрузить клиническую запись.',
  'record.forbidden': 'У вас нет прав на создание клинических записей.',
  'record.sessionEnded': 'Ваш сеанс завершён. Войдите снова, чтобы продолжить.',
  'record.gone': 'Эта клиническая запись больше недоступна.',
  'record.invalidInput': 'Сервер отклонил эту запись. Проверьте поля и попробуйте снова.',
  'record.saveFailed': 'Не удалось сохранить запись. Попробуйте снова.',
  'record.workplaceRequired': 'Для этого документа нужно выбрать место работы.',
  'record.workplaceUnavailable': 'Место работы этого документа больше недоступно для записи. Для продолжения нужно изменить контекст автора.',
  'record.authorWorkplace': 'Место работы автора',
  'record.changeWorkplace': 'Изменить место работы автора',
  'record.workplaceNotRecorded': 'Не сохранено',
  'record.workplaceUnsavedIntent': 'Место работы автора медицинского документа',
  'record.workplaceProtocolIncompatible': 'Для выбранного места работы нужен другой протокол. Документ и введённые данные не изменены.',
  'record.noRelease': 'Опубликованный протокол недоступен.',
  'record.noReleaseDetail': 'Прежде чем писать записи, необходимо опубликовать клинический протокол.',
  'record.invalidSchema': 'Данная версия приложения не может отобразить опубликованный протокол.',
  'record.unsupportedField': 'Этот тип поля не поддерживается данной версией',
  'record.fieldRequired': 'Это поле обязательно, прежде чем запись можно финализировать.',
  'record.codedSearch': 'Поиск в терминологическом словаре',
  'record.codedClear': 'Очистить кодированное значение',
  'record.choiceUnanswered': 'Не указано',
  'record.codedUnavailable': 'Справочник недоступен. Текущий ответ сохранён.',
  'record.referenceNone': 'Услуга не выбрана',
  'record.referenceUnavailable': 'Услуги клиники недоступны. Текущий выбор сохранён.',
  'record.create': 'Создать запись',
  'record.save': 'Сохранить',
  'record.conflict': 'Эта запись была изменена в другом месте. Загрузите сохранённую редакцию, чтобы продолжить; ваши несохранённые правки будут отброшены.',
  'record.reloadServerVersion': 'Загрузить сохранённую редакцию',
  'record.stateNew': 'Не создана',
  'record.stateSaved': 'Сохранена',
  'record.stateUnsaved': 'Несохранённые изменения',
  'record.unsavedIntent': 'Клиническая запись — правки, которые не были сохранены',
  'record.primaryUnsavedIntent': 'Основной протокол — правки, которые не были сохранены',
  'record.primaryTranslationUnsavedIntent': 'Перевод основного протокола — правки, которые не были сохранены',
  'record.stateSaving': 'Сохранение…',
  'record.stateFinal': 'Финальная',
  'record.printLoading': 'Загрузка печатных форм',
  'record.printPdf': 'Распечатать PDF',
  'record.printLanguage': 'Язык печати',
  'record.printingPdf': 'Подготовка PDF…',
  'record.printDownloaded': 'PDF скачан.',
  'record.printFailed': 'Не удалось подготовить PDF. Попробуйте снова.',
  'record.printForbidden': 'У вас нет прав на печать этой записи.',
  'record.printUnavailable': 'Эта запись не готова к печати.',
  'record.printConflict': 'Этот запрос на печать изменился во время подготовки. Попробуйте снова.',
  'record.dictationOpen': 'Диктовать',
  'record.dictationTitle': 'Диктовка и ИИ-помощник',
  'record.dictationIntro': 'Проговорите темы протокола. После остановки запись отправляется в OpenAI для расшифровки и извлечения полей.',
  'record.dictationTopics': 'Предлагаемые темы для проговаривания, из протокола этой записи:',
  'record.dictationNoTopics': 'В этом протоколе нет полей, способных принять надиктованный текст.',
  'record.dictationSourceLanguage': 'Язык речи',
  'record.dictationOutputLanguage': 'Язык записи',
  'record.dictationLanguageAuto': 'Определить автоматически',
  'record.dictationStart': 'Начать запись',
  'record.dictationRetry': 'Записать снова',
  'record.dictationStop': 'Остановить',
  'record.dictationTimer': 'Время записи',
  'record.dictationRequesting': 'Запрос на доступ к микрофону…',
  'record.dictationRecording': 'Запись…',
  'record.dictationProcessing': 'Расшифровка и раскладка полей протокола…',
  'record.dictationReady': 'Проверьте расшифровку и извлечённые поля перед применением.',
  'record.dictationTranscript': 'Расшифровка',
  'record.dictationExtracted': 'Извлечённые поля',
  'record.dictationNoExtracted': 'Содержимое полей протокола не распознано.',
  'record.dictationUnassigned': 'Содержимое, не отнесённое к полю',
  'record.dictationWarnings': 'Предупреждения',
  'record.dictationApply': 'Применить к черновику',
  'record.dictationPermissionDenied': 'Доступ к микрофону запрещён. Разрешите доступ и попробуйте снова.',
  'record.dictationMicrophoneFailed': 'Не удалось включить микрофон.',
  'record.dictationUnsupportedBrowser': 'Этот браузер не может записывать аудио для диктовки.',
  'record.dictationKeyMissing': 'Добавьте свой ключ API OpenAI в настройках профиля, прежде чем пользоваться диктовкой.',
  'record.dictationSchemaChanged': 'Протокол изменился. Закройте это окно и перезагрузите запись.',
  'record.dictationTooLarge': 'Запись больше {max} МБ. Запишите более короткий протокол.',
  'record.dictationUnsupportedAudio': 'Этот браузер выдал аудиофайл неподдерживаемого формата.',
  'record.dictationForbidden': 'У вас нет прав использовать диктовку для этой записи.',
  'record.dictationFailed': 'Не удалось обработать запись. Попробуйте снова.',
  'record.dictationClose': 'Закрыть',
  'correction.alreadyCorrected': 'Эта запись уже заменена кем-то другим. Откройте историю исправлений, чтобы прочитать замену — ваше изменение не применено.',
  'correction.idempotencyConflict': 'Это исправление уже было отправлено с другими данными. Ничего не заменено. Начните исправление заново, а не отправляйте повторно.',
  'correction.changedConcurrently': 'Эта запись изменилась, пока вы её читали. Перезагрузите её перед исправлением.',
  'correction.forbidden': 'У вас нет прав исправлять эту запись.',
  'correction.gone': 'Эта запись больше недоступна.',
  'correction.invalidInput': 'Проверьте причину исправления.',
  'correction.failed': 'Не удалось завершить исправление.',
  'correction.historyLoading': 'Проверка истории исправлений',
  'correction.historyUnavailable': 'Не удалось прочитать историю исправлений.',
  'correction.wasReplaced': 'Эта запись была заменена',
  'correction.replacesAnother': 'Эта запись заменяет более раннюю',
  'correction.reason': 'Причина исправления',
  'correction.correctedAt': 'Исправлено',
  'correction.openReplacement': 'Открыть замену',
  'correction.openOriginal': 'Открыть заменённую запись',
  'correction.amend': 'Исправить запись',
  'correction.markEnteredInError': 'Отметить как внесённое по ошибке',
  'correction.showHistory': 'История',
  'correction.amendTitle': 'Исправить этот протокол',
  'correction.amendBody': 'Выберите причину. Текущий документ останется без изменений со статусом «заменён», а новое редактируемое исправление начнётся с тем же содержимым.',
  'correction.enteredErrorTitle': 'Отметить этот протокол как внесённый по ошибке',
  'correction.enteredErrorBody': 'Выберите причину. Документ перестанет быть текущим, и замена создана не будет.',
  'correction.amending': 'Создание исправления…',
  'correction.markingEnteredError': 'Отметка как внесённого по ошибке…',
  'correction.reasonClinicalError': 'Клиническое исправление',
  'correction.reasonWrongPatient': 'Не тот пациент',
  'correction.reasonWrongProtocol': 'Неправильный протокол',
  'correction.reasonMissingInformation': 'Отсутствует информация',
  'correction.reasonDuplicateEntry': 'Дублирующий протокол',
  'correction.reasonOther': 'Другая причина',
  'correction.reasonDetail': 'Причина своими словами',
  'correction.historyTitle': 'История протокола',
  'correction.historyEmpty': 'Архивных редакций нет.',
  'correction.historyEnteredError': 'Введено по ошибке',
  'correction.historyAmended': 'Исправлено заменой',
  'correction.confirmAmendment': 'Подтвердить исправление',
  'correction.cancel': 'Оставить запись как есть',
  'record.fieldEmpty': 'Не записано',
  'record.unrepresentedTitle': 'Содержимое, которое эта версия не может отобразить',
  'record.unrepresentedDetail': 'Сохранённый протокол содержит эти значения, а приложение не знает, куда их поместить. Они перечислены, чтобы ничего не пропало незаметно.',
  'record.draftNotice': 'Это черновик. Это не подписанное клиническое свидетельство.',
  'record.evidence': 'Финализация',
  'record.finalizedAt': 'Финализирована',
  'record.protocol': 'Протокол',
  'record.protocolLoading': 'Загрузка протокола…',
  'record.protocolUnavailable': 'Название протокола недоступно',
  'record.status': 'Статус записи',

  'clinical.completeEntry': 'Завершить запись ЭМК',
  'clinical.completing': 'Завершение…',
  'clinical.completeConfirmTitle': 'Подтвердите завершение записи ЭМК',
  'clinical.completeConfirmBody': 'Завершение подтверждает все текущие протоколы, закрывает этот приём и его запись в расписании. Клинический приём не будет открыт заново.',
  'clinical.completeConfirm': 'Завершить окончательно',
  'clinical.completeCorrection': 'Завершить исправление',
  'clinical.completingCorrection': 'Завершение исправления…',
  'clinical.completeCorrectionConfirmTitle': 'Подтвердите завершение исправления',
  'clinical.completeCorrectionConfirmBody': 'Завершение подтверждает все текущие протоколы и закрывает рабочее пространство исправления. Завершённый приём и запись в расписании остаются без изменений.',
  'clinical.completeCorrectionConfirm': 'Завершить исправление',
  'clinical.completeCancel': 'Оставить запись открытой',
  'clinical.completeConflict': 'Не удалось завершить эту запись ЭМК, так как её клиническое состояние изменилось. Перезагрузите, чтобы увидеть текущее состояние.',
  'clinical.completeForbidden': 'У вас нет прав завершать записи ЭМК.',
  'clinical.completeSessionEnded': 'Ваш сеанс завершён. Войдите снова, чтобы продолжить.',
  'clinical.completeGone': 'Эта запись ЭМК больше недоступна.',
  'clinical.completeUnavailable': 'Сервис недоступен. Попробуйте чуть позже.',
  'clinical.completeFailed': 'Не удалось завершить запись ЭМК. Попробуйте снова.',
  'clinical.completeAlreadyFinished': 'Эта запись ЭМК уже завершена. Перезагрузите, чтобы увидеть текущее состояние.',
  'clinical.completeMainDiagnosisRequired': 'В этой записи нет основного диагноза. Запишите его в разделе «Диагнозы», затем завершите запись.',
  'clinical.completeProtocolInvalid': 'Один или несколько протоколов нельзя подтвердить в текущем виде. Заполните их обязательные поля и попробуйте снова.',
  'clinical.completeSaveProtocolsFirst': 'Сохраните все изменения протоколов перед завершением.',
  'clinical.completeEnteredInError': 'Эта запись ЭМК помечена как внесённая по ошибке и не может быть завершена.',
  'clinical.completeChangedConcurrently': 'Эта запись ЭМК изменилась в другом месте, пока этот экран был открыт. Перезагрузите, чтобы увидеть текущее состояние.',
  'clinical.completeNotFinishable': 'Эту запись ЭМК нельзя завершить в её текущем состоянии. Перезагрузите, чтобы увидеть её.',
  'clinical.completeScheduleNotCloseable': 'Не удалось закрыть связанную запись в расписании, поэтому ничего не завершено. Проверьте запись на приём и попробуйте снова.',
  'clinical.completeCorrectionNotOpen': 'Нет открытого исправления для завершения. Перезагрузите, чтобы увидеть текущее состояние документации.',

  'clinical.sectionDiagnoses': 'Диагнозы',
  'clinical.entryCompleted': 'Приём и его документация завершены. Протоколы доступны только для чтения, пока не начато исправление.',
  'clinical.entryCorrectionOpen': 'Идёт исправление документации. Клинический приём и запись в расписании остаются завершёнными.',
  'clinical.entryEnteredInError': 'Эта запись ЭМК помечена как внесённая по ошибке и доступна только для чтения.',
  'clinical.encounterStarted': 'Начат',
  'clinical.encounterType': 'Тип записи',
  'clinical.typeScheduled': 'Запланированный приём',
  'clinical.typeUnscheduled': 'Внеплановый приём',

  'encounterProtocols.title': 'Протоколы',
  'encounterProtocols.forbidden': 'У вас нет прав читать клинические записи.',
  'encounterProtocols.statusDraft': 'Черновик',
  'encounterProtocols.statusFinal': 'Финальный',
  'encounterProtocols.statusAmended': 'Изменён',
  'encounterProtocols.statusEnteredInError': 'Внесён по ошибке',

  'encounterNav.entryLabel': 'Эта запись ЭМК',
  'encounterNav.entryCodeLabel': 'Код записи ЭМК',
  'encounterNav.menuLabel': 'Разделы записи ЭМК',
  'encounterNav.addDescription': 'Начать ещё одну клиническую запись',
  'encounterNav.diagnosesDescription': 'Заключение этой записи',
  'encounterNav.referrals': 'Направления',
  'encounterNav.referralsDescription': 'Что было отправлено дальше отсюда',
  'encounterNav.services': 'Услуги',
  'encounterNav.servicesDescription': 'Оказанные услуги и их начисления',
  'encounterNav.protocolHistoryDescription': 'Архивные редакции протокола',
  'encounterNav.protocolHistoryMeta': 'История',
  'encounterNav.printGroupLabel': 'Распечатать PDF',

  'addProtocol.title': 'Добавить протокол',
  'addProtocol.detail': 'Новая запись создаётся пустой и сохраняется. Здесь ничего не финализируется.',
  'addProtocol.assignmentInactive': "Выбранный протокол недоступен для новых записей. Выберите другой протокол.",
  'addProtocol.legend': 'Какой протокол',
  'addProtocol.submit': 'Запустить протокол',
  'addProtocol.submitting': 'Запуск…',
  'addProtocol.alreadyStarted': 'Этот протокол уже запущен в этой записи. Закройте эту панель, чтобы увидеть его.',

  'referral.title': 'Направление',
  'referral.sectionTitle': 'Направления',
  'referral.emptyEncounter': 'По этой записи пока нет направлений.',
  'referral.emptyEncounterDetail': 'Направление оформляется из финализированной консультационной записи. Финализируйте запись, затем вернитесь сюда.',
  'referral.otherGroup': 'Направления из записей, не показанных выше',
  'referral.detailTitle': 'Подробности направления',
  'referral.loading': 'Загрузка направлений…',
  'referral.loadFailed': 'Не удалось загрузить направления.',
  'referral.forbidden': 'У вас нет прав работать с направлениями.',
  'referral.notFound': 'Это направление недоступно.',
  'referral.invalidInput': 'Сервер отклонил это направление. Проверьте поля и попробуйте снова.',
  'referral.saveFailed': 'Не удалось сохранить направление. Попробуйте снова.',
  'referral.conflict': 'Не удалось сохранить это направление, так как оно изменилось. Перезагрузите, чтобы увидеть текущее состояние.',
  'referral.changedConcurrently': 'Это направление было изменено в другом месте. Перезагрузите, чтобы увидеть сохранённую редакцию, прежде чем продолжать.',
  'referral.sourceMismatch': 'Это направление не относится к показанной здесь записи. Перезагрузите перед продолжением.',
  'referral.priority': 'Приоритет',
  'referral.priorityRoutine': 'Плановый',
  'referral.priorityUrgent': 'Срочный',
  'referral.specialty': 'Специальность',
  'referral.specialtyChoose': 'Выберите специальность',
  'referral.specialtiesLoading': 'Загрузка специальностей, которые принимает это направление…',
  'referral.specialtiesUnavailable': 'Не удалось загрузить специальности, которые принимает это направление, поэтому создать направление здесь пока нельзя.',
  'referral.recipient': 'Направить к',
  'referral.reason': 'Причина направления',
  'referral.note': 'Примечание (необязательно)',
  'referral.specialtyRequired': 'Необходимо указать специальность.',
  'referral.recipientRequired': 'Необходимо указать получателя.',
  'referral.reasonRequired': 'Необходимо указать причину направления.',
  'referral.create': 'Создать направление',
  'referral.newReferral': 'Новое направление',
  'referral.cancelEditor': 'Отмена',
  'referral.created': 'Направление {code} создано.',
  'referral.save': 'Сохранить направление',
  'referral.editDraft': 'Редактировать черновик',
  'referral.activate': 'Активировать направление',
  'referral.stateDraft': 'Черновик',
  'referral.stateActive': 'Активно',
  'referral.stateEnteredInError': 'Внесено по ошибке',
  'referral.activeImmutable': 'Это направление активно и больше не может быть изменено.',
  'referral.status': 'Статус направления',
  'referral.authoredAt': 'Создано',
  'referral.activatedAt': 'Активировано',
  'referral.sourceRecord': 'Исходная запись',
  'referral.sourceWorkplace': 'Из исходной записи',
  'addProtocol.inheritedWorkplace': 'Из приёма',
  'correction.unsavedIntent': 'Причина исправления меддокумента и место работы',

  'referral.sourceUnavailable': 'Запись, из которой оформлено это направление, недоступна.',
  'referral.sourceNotFinal': 'Эта запись больше не финальная, поэтому направление из неё оформить нельзя. Перезагрузите, чтобы увидеть текущее состояние.',
  'referral.sourceUnsupported': 'Из записи такого типа направление оформить нельзя.',
  'referral.specialtyUnsupported': 'Эта специальность не принимается для данного типа направления. Используйте ту, которую допускает определение направления.',

  'referral.discard': 'Отклонить черновик',
  'referral.discardConfirmBody': 'Отклонение отзывает этот черновик направления. Он сохраняется как отозванная запись с указанием причины, а не удаляется.',
  'referral.discardReason': 'Причина отклонения',
  'referral.discardReasonRequired': 'Прежде чем отклонить этот черновик, нужно указать причину.',
  'referral.discardConfirm': 'Отклонить окончательно',
  'referral.discardCancel': 'Сохранить черновик',
  'referral.unsavedIntent': 'Направление — сведения, которые не были сохранены',
  'referral.unsavedDiscardReason': 'Отзыв направления — причина, которая не была отправлена',
  'referral.idempotencyConflict': 'Этот отзыв уже записан с другой причиной. Перезагрузите, чтобы увидеть сохранённое.',
  'referral.notDraft': 'Это направление активировано и больше не может быть изменено. Перезагрузите, чтобы увидеть его.',

  'diagnosis.title': 'Диагнозы',
  'diagnosis.loading': 'Загрузка диагнозов…',
  'diagnosis.loadFailed': 'Не удалось загрузить контекст диагноза.',
  'diagnosis.forbidden': 'У вас нет прав читать диагнозы.',
  'diagnosis.empty': 'Диагнозы пока не записаны',
  'diagnosis.emptyDetail': 'Сначала запишите основной диагноз этого приёма.',
  'diagnosis.mainRequired': 'В этой записи ЭМК нет основного диагноза. Он обязателен для её завершения.',
  'diagnosis.mainRecorded': 'Для этой записи ЭМК записан основной диагноз.',
  'diagnosis.roleMain': 'Основной',
  'diagnosis.roleComplication': 'Осложнение',
  'diagnosis.roleConcomitant': 'Сопутствующий',
  'diagnosis.roleAdditional': 'Дополнительный',
  'diagnosis.clinicalActive': 'Активный',
  'diagnosis.clinicalInactive': 'Неактивный',
  'diagnosis.clinicalResolved': 'Разрешён',
  'diagnosis.verificationConfirmed': 'Подтверждён',
  'diagnosis.verificationProvisional': 'Предварительный',
  'diagnosis.verificationRefuted': 'Опровергнут',
  'diagnosis.verificationEnteredInError': 'Внесён по ошибке',
  'diagnosis.mergedMark': 'Объединён',
  'diagnosis.listLabel': 'Зарегистрированные диагнозы',
  'diagnosis.onset': 'Начало',
  'diagnosis.recordedAt': 'Записан',
  'diagnosis.note': 'Примечание',
  'diagnosis.add': 'Добавить',
  'diagnosis.addMain': 'Добавить основной диагноз',
  'diagnosis.addComplication': 'Добавить осложнение',
  'diagnosis.addConcomitant': 'Добавить сопутствующий диагноз',
  'diagnosis.addAdditional': 'Добавить дополнительный диагноз',
  'diagnosis.addTitle': 'Новый диагноз',
  'diagnosis.editTitle': 'Изменить диагноз',
  'diagnosis.edit': 'Изменить',
  'diagnosis.editNamed': 'Изменить {name}',
  'diagnosis.remove': 'Удалить',
  'diagnosis.removeNamed': 'Удалить {name}',
  'diagnosis.fieldRole': 'Роль',
  'diagnosis.fieldDisplay': 'Кодированный диагноз',
  'diagnosis.searchPlaceholder': 'Поиск по коду или названию диагноза',
  'diagnosis.searchHint': 'Для поиска введите не менее двух символов.',
  'diagnosis.searching': 'Поиск диагнозов…',
  'diagnosis.searchFailed': 'Терминология диагнозов недоступна.',
  'diagnosis.searchEmpty': 'Ни один диагноз не соответствует этому поиску.',
  'diagnosis.searchResults': 'Результаты поиска диагнозов',
  'diagnosis.fieldOnset': 'Дата начала',
  'diagnosis.fieldNote': 'Примечание',
  'diagnosis.fieldClinicalStatus': 'Клинический статус',
  'diagnosis.fieldVerification': 'Верификация',
  'diagnosis.attributesTitle': 'Сведения о диагнозе',
  'diagnosis.fieldDiagnosisKind': 'Вид диагноза',
  'diagnosis.fieldDiseaseNature': 'Характер заболевания',
  'diagnosis.fieldBicausalType': 'Дополнительный тип',
  'diagnosis.fieldClinicalGroup': 'Клиническая группа',
  'diagnosis.fieldTopography': 'Топография ICD-O-3',
  'diagnosis.fieldMorphology': 'Морфология ICD-O-3',
  'diagnosis.fieldTNM': 'TNM',
  'diagnosis.fieldRecurrence': 'Рецидив',
  'diagnosis.fieldProgression': 'Прогрессирование',
  'diagnosis.selectPlaceholder': 'Выберите',
  'diagnosis.kindPreliminary': 'Предварительный',
  'diagnosis.kindClinical': 'Клинический',
  'diagnosis.kindFinal': 'Окончательный',
  'diagnosis.natureAcute': 'Острое',
  'diagnosis.natureChronicFirst': 'Хроническое, впервые выявленное',
  'diagnosis.natureChronicEstablished': 'Хроническое, ранее установленное',
  'diagnosis.groupI': 'I',
  'diagnosis.groupIA': 'IA',
  'diagnosis.groupIB': 'IB',
  'diagnosis.groupIPlus': 'I+',
  'diagnosis.groupII': 'II',
  'diagnosis.groupIIA': 'IIA',
  'diagnosis.groupIII': 'III',
  'diagnosis.groupIV': 'IV',
  'diagnosis.bicausalCompeting': 'Конкурирующий',
  'diagnosis.bicausalCombined': 'Сочетанный',
  'diagnosis.bicausalBackground': 'Фоновый',
  'diagnosis.oncologyTitle': 'Онкология',
  'diagnosis.oncologyCategoryMalignant': 'Злокачественное новообразование',
  'diagnosis.oncologyCategoryBenign': 'Доброкачественное новообразование',
  'diagnosis.oncologyCategoryInSitu': 'Новообразование in situ',
  'diagnosis.oncologyCategoryUncertain': 'Неопределённое поведение',
  'diagnosis.oncologyCategoryUnspecified': 'Неуточнённый характер',
  'diagnosis.oncologyFlags': 'Онкологические признаки',
  'diagnosis.topographyAuto': 'Получено из кода диагноза.',
  'diagnosis.kindRequired': 'Выберите вид диагноза.',
  'diagnosis.natureRequired': 'Выберите характер заболевания.',
  'diagnosis.bicausalRequired': 'Выберите дополнительный тип диагноза.',
  'diagnosis.clinicalGroupRequired': 'Выберите клиническую группу.',
  'diagnosis.topographyRequired': 'Выберите топографию ICD-O-3.',
  'diagnosis.morphologyRequired': 'Выберите морфологию ICD-O-3.',
  'diagnosis.oncologyChecking': 'Проверка онкологических требований…',
  'diagnosis.oncologyUnavailable': 'Онкологическая терминология недоступна.',
  'diagnosis.tnmChecking': 'Загрузка параметров TNM…',
  'diagnosis.tnmUnavailable': 'Варианты TNM недоступны.',
  'diagnosis.tnmRequired': 'Выберите TNM.',
  'diagnosis.selectionRequired': 'Выберите диагноз из клинической терминологии.',
  'diagnosis.save': 'Сохранить диагноз',
  'diagnosis.saving': 'Сохранение…',
  'diagnosis.cancel': 'Отмена',
  'diagnosis.unsavedIntent': 'Диагноз \u2014 правки, которые не были сохранены',

  'diagnosis.reviewTitle': 'Проверить этот контекст',
  'diagnosis.reviewDetail': 'Записывает, кто и когда проверил диагнозы. Сами диагнозы не меняются.',
  'diagnosis.staleTitle': 'Этот контекст диагнозов устарел',
  'diagnosis.staleDetail': 'Он изменился после последней проверки. Примите его как есть, подтвердите или объедините диагнозы в разрешённый контекст.',
  'diagnosis.accept': 'Принять',
  'diagnosis.confirm': 'Подтвердить',
  'diagnosis.merge': 'Объединить',
  'diagnosis.reason': 'Причина (необязательно)',
  'diagnosis.decisionSubmit': 'Записать решение',
  'diagnosis.decisionCancel': 'Отмена',
  'diagnosis.mergeTitle': 'Объединить контекст диагноза',
  'diagnosis.mergeDetail': 'Переформулируйте эти диагнозы как объединённый контекст. Пропущенный диагноз не переформулируется и сохраняет свой текущий вид.',
  'diagnosis.mergeRestate': 'Переформулировать {name}',
  'diagnosis.mergeEmpty': 'Выберите хотя бы один диагноз для переформулировки.',
  'diagnosis.mergeSubmit': 'Объединить и записать',
  'diagnosis.contextStatusCurrent': 'Текущий',
  'diagnosis.contextStatusStale': 'Устарел',
  'diagnosis.contextStatusAccepted': 'Принят',
  'diagnosis.contextStatusConfirmed': 'Подтверждён',
  'diagnosis.decisionsTitle': 'Решения проверки',
  'diagnosis.decisionAccept': 'Принят',
  'diagnosis.decisionConfirm': 'Подтверждён',
  'diagnosis.decisionMerge': 'Объединён',
  'diagnosis.readOnly': 'Диагнозы этой записи ЭМК доступны только для чтения.',
  'diagnosis.readOnlyDetail': 'Завершённый или внесённый по ошибке приём оставляет диагнозы читаемыми и отклоняет изменения.',
  'diagnosis.mainConflict': 'У этого приёма уже есть основной диагноз. Измените существующий или запишите этот в другой роли.',
  'diagnosis.changedConcurrently': 'Контекст диагнозов изменился в другом месте, пока этот экран был открыт. Перезагрузите, чтобы увидеть текущее состояние.',
  'diagnosis.writeReadOnly': 'Этот приём больше не принимает изменения диагнозов.',
  'diagnosis.writeForbidden': 'У вас нет прав изменять диагнозы.',
  'diagnosis.writeSessionEnded': 'Ваш сеанс завершён. Войдите снова, чтобы продолжить.',
  'diagnosis.writeGone': 'Этот приём больше недоступен.',
  'diagnosis.writeUnavailable': 'Сервис недоступен. Попробуйте чуть позже.',
  'diagnosis.writeInvalid': 'Выберите диагноз из клинической терминологии и попробуйте снова.',
  'diagnosis.writeAttributesInvalid': 'Заполните обязательные сведения о диагнозе и попробуйте снова.',
  'diagnosis.writeFailed': 'Не удалось сохранить диагноз. Попробуйте снова.',
  'diagnosis.reload': 'Перезагрузить контекст диагнозов',

  'diagnosis.dictionaryTitle': 'Словарь диагнозов',
  'diagnosis.dictionaryTitleTopography': 'Топография ICD-O-3',
  'diagnosis.dictionaryTitleMorphology': 'Морфология ICD-O-3',
  'diagnosis.dictionaryTabCatalogueICD10': 'МКБ-10',
  'diagnosis.dictionaryTabCatalogueICD9IL': 'МКБ-9 IL',
  'diagnosis.dictionarySearchPlaceholder': 'Код или название',
  'diagnosis.dictionaryTopographyPlaceholder': 'Код топографии или локализация',
  'diagnosis.dictionaryMorphologyPlaceholder': 'Морфологический код или гистология',
  'diagnosis.dictionarySearchResults': 'Результаты поиска диагнозов',
  'diagnosis.dictionaryBrowseHint': 'Просмотр дерева. Введите три символа или префикс кода для поиска по всему словарю.',
  'diagnosis.dictionarySearching': 'Поиск в словаре…',
  'diagnosis.dictionaryLoading': 'Загрузка…',
  'diagnosis.dictionaryLoadFailed': 'Не удалось прочитать словарь.',
  'diagnosis.dictionaryEmptyLevel': 'Под этим кодом ничего нет.',
  'diagnosis.sectionCount': 'Записано: {count}',
  'diagnosis.sectionEmpty': 'Ничего не записано.',
  'diagnosis.secondaryBlocked': 'Сначала запишите основной диагноз.',
  /*
   * QUICK ENTRIES — the Encounter as a phone offers it (#372). See the English
   * module for why these are the entry's vocabulary rather than the record's.
   */
  'encounterQuick.title': 'Быстрая запись',
  'encounterQuick.deskOnly':
    'Здесь приём доступен только для чтения. Редактор протокола, диктовка, направления, услуги и печатные формы — на компьютере.',
  'encounterQuick.note': 'Дописать в протокол',
  'encounterQuick.vitals': 'Внести витальные показатели',
  'encounterQuick.task': 'Создать задачу',
  'encounterQuick.save': 'Сохранить',
  'encounterQuick.saving': 'Сохранение…',
  'encounterQuick.saveFailed': 'Сохранить не удалось. Попробуйте ещё раз.',
  'encounterQuick.saveForbidden': 'У вас нет прав на это действие.',
  'encounterQuick.saveUnavailable': 'Сервер не ответил. Попробуйте ещё раз.',
  'encounterQuick.noteInto': 'Дописывается в: {protocol}',
  'encounterQuick.noteProtocol': 'Протокол',
  'encounterQuick.noteChoose': 'Выберите протокол',
  'encounterQuick.noteNowhere':
    'Все протоколы этого приёма подписаны. Исправить один или начать другой можно на компьютере.',
  'encounterQuick.noteNoFreeText':
    'В этом протоколе нет текстового поля, поэтому дописать сюда предложение нельзя.',
  'encounterQuick.noteConflict':
    'Протокол изменился, пока была открыта панель. Закройте её, откройте протокол заново и введите текст ещё раз.',
  'encounterQuick.protocolUnwritten': 'Этот протокол на приёме ещё не заполнялся.',
  'encounterQuick.vitalLoading': 'Загружается список того, что можно внести…',
  'encounterQuick.vitalCatalogueFailed': 'Не удалось прочитать список витальных показателей.',
  'encounterQuick.vitalEmpty': 'В этой сборке нельзя внести ни один витальный показатель.',
  'encounterQuick.vitalNothing': 'Введите хотя бы одно измерение.',
  'encounterQuick.vitalPartial': 'Вносится целиком: заполните все части — {name} — или очистите их.',
  'encounterQuick.vitalNotNumber': 'Не число: {name}',
  'encounterQuick.vitalRefused': 'Сервер отклонил эти измерения. Проверьте их и попробуйте ещё раз.',
  'encounterQuick.taskNoPractitioner':
    'Ваша учётная запись не связана с врачом, поэтому её нельзя назначить ответственной за задачу.',
  "record.patientDifference": "Данные пациента отличаются от сохранённых в протоколе.",
  "record.comparePatient": "Посмотреть различия",
  "record.patientComparisonUnavailable": "Сравнить сохранённые и текущие данные пациента не удалось. Протокол доступен для просмотра.",
  "record.patientNameThen": "ФИО в протоколе",
  "record.patientNameNow": "ФИО в текущей карточке",
  "record.patientBirthDateThen": "Дата рождения в протоколе (возраст на дату приёма)",
  "record.patientBirthDateNow": "Дата рождения в карточке (возраст на дату приёма)",
  "record.patientCorrectionContext": "Исправление создаст новую редакцию с актуальным контекстом пациента, включая анамнез. Проверьте её перед завершением исправления: оно подтверждает текущий комплект протоколов приёма.",
  "record.keepPatientSnapshot": "Оставить как есть",
}
